import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import { MapContainer, ImageOverlay, useMap, useMapEvents } from 'react-leaflet';
import { CRS } from 'leaflet';
import { usePermissionState } from 'helpers/permissionHelper';
import { getAllStations } from 'services/productionOverviewService';
import { FACTORY_MAP_IMAGE_URL, FACTORY_MAP_BOUNDS, FACTORY_MAP_IMAGE_HEIGHT, getFocusedZoom } from './qualityFactoryMapConfig';
import { loadZoneLayout, saveZoneLayout, resetZoneLayout, loadStationZoneMap, saveStationZoneMap } from './qualityZonesConfig';
import QualityZonesLayer from './QualityZonesLayer';
import QualityCoordinateGrid from './QualityCoordinateGrid';
import QualityStageZoneModal from './QualityStageZoneModal';

function CursorCoordinates({ onMove }) {
  useMapEvents({
    mousemove(e) {
      onMove({ x: Math.round(e.latlng.lng), y: Math.round(e.latlng.lat) });
    },
    mouseout() {
      onMove(null);
    }
  });
  return null;
}

CursorCoordinates.propTypes = {
  onMove: PropTypes.func.isRequired
};

function InitialMapView() {
  const map = useMap();
  useEffect(() => {
    map.setView([FACTORY_MAP_IMAGE_HEIGHT, 0], getFocusedZoom(map), {
      animate: false
    });
  }, []);
  return null;
}

function getStationId(station) {
  return station.stationId ?? station.StationId ?? station.id ?? station.Id;
}

function getRecordStationId(record) {
  return record.stationId ?? record.StationId;
}

export default function QualityFactoryMap({ stageGroups, onStageClick }) {
  const { isFullPermission: canEditZones } = usePermissionState();
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [zoneSaving, setZoneSaving] = useState(false);
  const [zoneEditMode, setZoneEditMode] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const [showGrid, setShowGrid] = useState(false);
  const [cursorCoord, setCursorCoord] = useState(null);
  const [stationZoneMap, setStationZoneMap] = useState({});
  const [stationZoneMapLoading, setStationZoneMapLoading] = useState(true);
  const [stations, setStations] = useState([]);
  const [showStageZoneModal, setShowStageZoneModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadZoneLayout()
      .then(loaded => {
        if (!cancelled) setZones(loaded);
      })
      .finally(() => {
        if (!cancelled) setZonesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadStationZoneMap()
      .then(loaded => {
        if (!cancelled) setStationZoneMap(loaded);
      })
      .finally(() => {
        if (!cancelled) setStationZoneMapLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getAllStations()
      .then(result => {
        const records = result?.data?.data || result?.data || [];
        if (!cancelled) setStations(Array.isArray(records) ? records : []);
      })
      .catch(err => console.error('Error fetching stations', err));
    return () => {
      cancelled = true;
    };
  }, []);

  const stationPathById = useMemo(() => {
    const map = {};
    stations.forEach(s => {
      const id = getStationId(s);
      const path = s.stationPath ?? s.StationPath ?? s.path ?? s.Path;
      if (id != null && path) map[id] = path;
    });
    return map;
  }, [stations]);

  const zoneIdByCode = useMemo(() => {
    const map = {};
    zones.forEach(z => {
      map[z.code] = z.id;
    });
    return map;
  }, [zones]);

  const resolveZoneCodeForRecord = useCallback(
    record => {
      const stationId = getRecordStationId(record);
      if (stationId == null) return null;
      const path = stationPathById[stationId];
      if (!path) return null;
      return stationZoneMap[path] || null;
    },
    [stationPathById, stationZoneMap]
  );

  const stagesByZone = useMemo(() => {
    const counts = {};
    stageGroups.forEach(g => {
      (g.records || []).forEach(record => {
        const zoneCode = resolveZoneCodeForRecord(record);
        if (!zoneCode) return;
        const zoneId = zoneIdByCode[zoneCode];
        if (!zoneId) return;
        if (!counts[zoneId]) counts[zoneId] = {};
        counts[zoneId][g.name] = (counts[zoneId][g.name] || 0) + 1;
      });
    });

    const map = {};
    Object.entries(counts).forEach(([zoneId, byName]) => {
      map[zoneId] = Object.entries(byName).map(([name, count]) => ({
        name,
        count
      }));
    });
    return map;
  }, [stageGroups, zoneIdByCode, resolveZoneCodeForRecord]);

  const maxCount = useMemo(
    () =>
      Object.values(stagesByZone).reduce(
        (max, list) =>
          Math.max(
            max,
            list.reduce((sum, s) => sum + s.count, 0)
          ),
        0
      ),
    [stagesByZone]
  );

  const handleStageZoneSave = useCallback(async nextMap => {
    setStationZoneMap(nextMap);
    await saveStationZoneMap(nextMap);
  }, []);

  const persistZones = useCallback(async nextZones => {
    setZoneSaving(true);
    await saveZoneLayout(nextZones);
    setZoneSaving(false);
  }, []);

  const handleZoneChange = useCallback((id, rect) => {
    setZones(prev => prev.map(z => (z.id === id ? rect : z)));
  }, []);

  const handleZoneCommit = useCallback(
    (id, rect) => {
      setZones(prev => {
        const next = prev.map(z => (z.id === id ? rect : z));
        persistZones(next);
        return next;
      });
    },
    [persistZones]
  );

  const handleZoneNumberChange = useCallback((id, field, value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return;
    setZones(prev => prev.map(z => (z.id === id ? { ...z, [field]: num } : z)));
  }, []);

  const handleZoneNumberBlur = useCallback(() => {
    persistZones(zones);
  }, [zones, persistZones]);

  const handleResetZones = useCallback(async () => {
    setZoneSaving(true);
    const defaults = await resetZoneLayout();
    setZones(defaults);
    setZoneSaving(false);
  }, []);

  const handleCopyZones = useCallback(() => {
    const json = JSON.stringify(zones, null, 2);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(json)
        .then(() => setCopyStatus('Copied!'))
        .catch(() => setCopyStatus('Copy failed'));
    } else {
      setCopyStatus('Clipboard unavailable');
    }
    setTimeout(() => setCopyStatus(''), 2000);
  }, [zones]);

  return (
    <div className="quality-factory-map-wrapper" style={{ borderRadius: 6, padding: 16 }}>
      {canEditZones && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 8
          }}
        >
          <button
            type="button"
            className={`quality-view-toggle-btn ${zoneEditMode ? 'is-active' : ''}`}
            style={{
              padding: '5px 12px',
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 6,
              cursor: 'pointer'
            }}
            onClick={() => {
              setZoneEditMode(v => {
                const next = !v;
                setShowGrid(next);
                return next;
              });
            }}
          >
            {zoneEditMode ? 'Done Editing Zones' : 'Edit Zones'}
          </button>
          {!zoneEditMode && (
            <button
              type="button"
              className="quality-view-toggle-btn"
              style={{
                padding: '5px 12px',
                fontSize: 12,
                fontWeight: 500,
                borderRadius: 6,
                cursor: 'pointer'
              }}
              onClick={() => setShowStageZoneModal(true)}
            >
              Config Zone-Stage
            </button>
          )}
          {zoneEditMode && (
            <>
              <button
                type="button"
                className={`quality-view-toggle-btn ${showGrid ? 'is-active' : ''}`}
                style={{
                  padding: '5px 12px',
                  fontSize: 12,
                  fontWeight: 500,
                  borderRadius: 6,
                  cursor: 'pointer'
                }}
                onClick={() => setShowGrid(v => !v)}
              >
                {showGrid ? 'Hide Grid' : 'Show Grid'}
              </button>
              <button
                type="button"
                disabled={zoneSaving}
                style={{
                  padding: '5px 12px',
                  fontSize: 12,
                  borderRadius: 6,
                  border: '1px solid #dee2e6',
                  background: '#fff',
                  cursor: zoneSaving ? 'default' : 'pointer',
                  opacity: zoneSaving ? 0.6 : 1
                }}
                onClick={handleResetZones}
              >
                Reset to defaults
              </button>
              <button
                type="button"
                style={{
                  padding: '5px 12px',
                  fontSize: 12,
                  borderRadius: 6,
                  border: '1px solid #dee2e6',
                  background: '#fff',
                  cursor: 'pointer'
                }}
                onClick={handleCopyZones}
              >
                Copy coordinates (JSON)
              </button>
              {copyStatus && <span style={{ fontSize: 12, color: '#6c757d' }}>{copyStatus}</span>}
              {zoneSaving && (
                <span
                  style={{
                    fontSize: 12,
                    color: '#2c7be5',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  <Spinner animation="border" size="sm" /> Saving...
                </span>
              )}
            </>
          )}
        </div>
      )}

      <div
        className={`quality-factory-map-canvas ${zoneEditMode ? 'is-editing-zones' : ''}`}
        style={{ borderRadius: 6, overflow: 'hidden', position: 'relative' }}
      >
        {zonesLoading || stationZoneMapLoading ? (
          <div
            style={{
              height: 560,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            {showGrid && (
              <div
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1000,
                  fontSize: 12,
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  color: '#2c7be5',
                  background: 'rgba(255,255,255,0.9)',
                  padding: '4px 8px',
                  borderRadius: 6,
                  border: '1px solid #2c7be5',
                  pointerEvents: 'none'
                }}
              >
                {cursorCoord ? `X: ${cursorCoord.x}  Y: ${cursorCoord.y}` : 'Move mouse over map'}
              </div>
            )}
            <MapContainer
              crs={CRS.Simple}
              bounds={FACTORY_MAP_BOUNDS}
              maxBounds={FACTORY_MAP_BOUNDS}
              maxBoundsViscosity={0.8}
              minZoom={-4}
              maxZoom={4}
              zoomSnap={0.25}
              attributionControl={false}
              style={{ height: 560, width: '100%' }}
            >
              <ImageOverlay url={FACTORY_MAP_IMAGE_URL} bounds={FACTORY_MAP_BOUNDS} />
              <InitialMapView />
              {showGrid && <CursorCoordinates onMove={setCursorCoord} />}
              {showGrid && <QualityCoordinateGrid />}

              <QualityZonesLayer
                zones={zones}
                editing={zoneEditMode && canEditZones}
                onZoneChange={handleZoneChange}
                onZoneCommit={handleZoneCommit}
                stagesByZone={stagesByZone}
                maxCount={maxCount}
                onStageClick={onStageClick}
              />
            </MapContainer>
          </>
        )}
      </div>

      {zoneEditMode && canEditZones && (
        <div
          className="quality-factory-map-unplaced"
          style={{
            marginTop: 8,
            padding: '10px 12px',
            borderRadius: 6,
            maxHeight: 220,
            overflowY: 'auto'
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#8a6116',
              marginBottom: 8
            }}
          >
            Drag a zone label to move it, drag the blue square to resize — or type exact numbers below.
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(120px,1fr) repeat(4, 70px)',
              gap: 6,
              alignItems: 'center',
              fontSize: 11
            }}
          >
            <span style={{ fontWeight: 700 }}>Zone</span>
            <span style={{ fontWeight: 700 }}>X</span>
            <span style={{ fontWeight: 700 }}>Y</span>
            <span style={{ fontWeight: 700 }}>W</span>
            <span style={{ fontWeight: 700 }}>H</span>
            {zones.map(zone => (
              <React.Fragment key={zone.id}>
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {zone.label}
                </span>
                {['x', 'y', 'w', 'h'].map(field => (
                  <input
                    key={field}
                    type="number"
                    value={Math.round(zone[field])}
                    onChange={e => handleZoneNumberChange(zone.id, field, e.target.value)}
                    onBlur={handleZoneNumberBlur}
                    disabled={zoneSaving}
                    style={{
                      width: 62,
                      fontSize: 11,
                      padding: '2px 4px',
                      border: '1px solid #dee2e6',
                      borderRadius: 4
                    }}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      <div className="quality-factory-map-hint" style={{ fontSize: 12, textAlign: 'center', marginTop: 8 }}>
        Each zone shows its own name and the total rejected logs mapped to it — click a zone to view all of them. Each rejected log is matched to a station
        strictly by its stationId (no name-based fallback, to avoid silent mismatches) — logs without a stationId, or whose station has no zone assigned yet,
        are not shown.
        {canEditZones && ' Use "Config Zone-Stage" to assign a station to a zone.'}
      </div>

      {canEditZones && (
        <QualityStageZoneModal
          show={showStageZoneModal}
          onHide={() => setShowStageZoneModal(false)}
          zones={zones}
          stations={stations}
          stageGroups={stageGroups}
          stationZoneMap={stationZoneMap}
          onSave={handleStageZoneSave}
        />
      )}
    </div>
  );
}

QualityFactoryMap.propTypes = {
  stageGroups: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      records: PropTypes.array
    })
  ).isRequired,
  onStageClick: PropTypes.func.isRequired
};
